import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useClientAuth } from "@/context/ClientAuthContext";
import Spade from "@/components/Spade";
import { PORTAL } from "@/constants/testIds";
import { ArrowRight } from "lucide-react";

const headlineWords = ["Your", "growth,", "illuminated."];

export default function ClientLogin() {
    const navigate = useNavigate();
    const { login, status } = useClientAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (status === "authed") navigate("/portal/dashboard", { replace: true });
    }, [status, navigate]);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        const res = await login(email.trim(), password);
        setLoading(false);
        if (res.ok) navigate("/portal/dashboard", { replace: true });
        else setError(res.error || "Login failed");
    };

    return (
        <div
            data-testid={PORTAL.loginRoot}
            className="min-h-screen grid lg:grid-cols-2 relative overflow-hidden bg-[#2a0303]"
        >
            {/* Left — kinetic brand panel */}
            <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden">
                <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#8B0A0A] via-[#4A0505] to-[#0d0000]" />
                <div className="absolute right-[-10%] top-1/2 -translate-y-1/2 opacity-[0.07]">
                    <Spade size={620} />
                </div>
                <div className="flex items-center gap-2">
                    <Spade size={26} />
                    <span className="font-display text-2xl text-[var(--text-primary)]">
                        Aestherium<span className="text-[var(--accent-gold)]">.</span>
                        <sup className="text-xs text-[var(--accent-gold)] ml-0.5">®</sup>
                    </span>
                </div>

                <div>
                    <p className="text-xs uppercase tracking-[0.4em] text-[var(--accent-gold)] mb-6">
                        Client Portal
                    </p>
                    <h1 className="font-display font-bold text-6xl xl:text-7xl leading-[0.95] text-[var(--text-primary)]">
                        {headlineWords.map((w, i) => (
                            <span key={i} className="block overflow-hidden">
                                <motion.span
                                    className="inline-block"
                                    initial={{ y: "110%" }}
                                    animate={{ y: 0 }}
                                    transition={{
                                        duration: 0.9,
                                        delay: 0.2 + i * 0.12,
                                        ease: [0.16, 1, 0.3, 1],
                                    }}
                                >
                                    {i === 2 ? (
                                        <span className="italic text-[var(--accent-champagne)]">
                                            {w}
                                        </span>
                                    ) : (
                                        w
                                    )}
                                </motion.span>
                            </span>
                        ))}
                    </h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.8, duration: 0.8 }}
                        className="mt-6 text-lg text-[var(--text-secondary)] max-w-md"
                    >
                        Your reach, engagement and sales momentum — updated weekly, in one
                        private dashboard.
                    </motion.p>
                </div>

                <p className="text-xs text-[var(--text-secondary)]/60 uppercase tracking-[0.3em]">
                    Organic Reach. Real Sales. Zero Ads.
                </p>
            </div>

            {/* Right — login form */}
            <div className="flex items-center justify-center p-6 md:p-12 relative">
                <div className="absolute inset-0 -z-10 lg:hidden bg-gradient-to-b from-[#4A0505] to-[#0d0000]" />
                <motion.form
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    onSubmit={submit}
                    className="w-full max-w-md surface-glass rounded-3xl border border-[var(--border-subtle)] p-8 md:p-10"
                >
                    <div className="flex items-center gap-2 mb-8 lg:hidden">
                        <Spade size={24} />
                        <span className="font-display text-xl text-[var(--text-primary)]">
                            Aestherium<span className="text-[var(--accent-gold)]">.</span>
                        </span>
                    </div>
                    <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent-gold)] mb-3">
                        Client Login
                    </p>
                    <h2 className="font-display text-4xl font-bold text-[var(--text-primary)] mb-8">
                        Welcome back.
                    </h2>

                    <label className="block text-xs uppercase tracking-[0.25em] text-[var(--accent-champagne)]/70 mb-2">
                        Email
                    </label>
                    <input
                        data-testid={PORTAL.loginEmail}
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full mb-5 bg-transparent border border-[var(--border-subtle)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)] transition-colors"
                    />

                    <label className="block text-xs uppercase tracking-[0.25em] text-[var(--accent-champagne)]/70 mb-2">
                        Password
                    </label>
                    <input
                        data-testid={PORTAL.loginPassword}
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="w-full mb-2 bg-transparent border border-[var(--border-subtle)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)] transition-colors"
                    />

                    <div className="flex justify-end mb-6">
                        <Link
                            data-testid={PORTAL.forgotLink}
                            to="/portal/forgot-password"
                            className="text-xs text-[var(--text-secondary)] hover:text-[var(--accent-champagne)] transition-colors"
                        >
                            Forgot password?
                        </Link>
                    </div>

                    {error && (
                        <p
                            data-testid={PORTAL.loginError}
                            className="mb-5 text-sm text-red-300 bg-red-900/30 border border-red-500/30 rounded-lg px-4 py-3"
                        >
                            {error}
                        </p>
                    )}

                    <button
                        data-testid={PORTAL.loginSubmit}
                        type="submit"
                        disabled={loading}
                        className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-[var(--accent-gold)] text-[var(--brand-dark)] font-semibold uppercase tracking-[0.2em] text-sm hover:bg-[var(--accent-champagne)] transition-colors disabled:opacity-70"
                    >
                        {loading ? "Logging in…" : "Log In"}
                        <ArrowRight size={16} />
                    </button>

                    <Link
                        to="/"
                        className="mt-6 block text-center text-xs text-[var(--text-secondary)] hover:text-[var(--accent-champagne)]"
                    >
                        ← Back to main site
                    </Link>
                </motion.form>
            </div>
        </div>
    );
}
